/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../widgets/livewidget/nls/StringResources',
	'../../dataPointActions/api/DataPointActionsProviderAPI',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], (APIFactory, StringResources, DataPointActionsProviderAPI, ContentActionsProviderAPI) => {

	class ContextualGridAction {

		constructor({ dashboardAPI, content, features }) {
			this.dashboard = dashboardAPI || features && features.API;
			this.features = features;
			if (content) {
				// dataPointAction will pass in the content api
				this.content = content;
				if (features) {
					features.DataPointActions.registerProvider('contextualGridAction', this.getAPI());
				}
			} else {
				features && features.ContentActions.registerProvider('contextualGridAction', this.getAPI());
			}
		}

		/**
		 * dataPoint actions
		 * @param {*} selections
		 * @param {*} options
		 */
		getDataPointActionList(selections, options) {
			const content = this.content;
			const bShowAllOptions = !options.noFilters;
			if (bShowAllOptions && this.dashboard.getMode() === this.dashboard.MODES.EDIT) {
				return this._getContributionList(content, true/*shouldCloseOnActionApplied*/, true/*isTextOnly*/);
			}
			return [];
		}

		_supportsRelationshipsView() {
			// relationships diagram is only available in authoring mode
			if (this.dashboard.getMode() === this.dashboard.MODES.EDIT) {
				// @todo: when removing the feature flag, the feature flag needs to be replaced with an app config (default: disabled)
				return this.dashboard.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'RelationshipsView', 'enabled') ||
					this.dashboard.getConfiguration('showRelationships');
			}
			return false;
		}

		_supportAction(idList, content) {
			// action is always supported when relationships is enabled
			if (!this._supportsRelationshipsView()) {
				const showInConsumeMode = this.dashboard.getCanvas().getPropertyValue('showDataGridInConsumeMode');
				const enableShowDataOfViz = this._isInteractionEnabled('showData') && (this.dashboard.getMode() === this.dashboard.MODES.EDIT || showInConsumeMode);
				if ((idList && idList.length > 1) || !enableShowDataOfViz) {
					//contexualGrid does not support multi-widget Selections
					return false;
				} else {
					const state = content.getFeature('state');
					if (state.getError()) {
						//don't show data when widget is in error state
						return false;
					}
				}
			}
			return true;
		}

		/**
		 * widgetAction interface
		 * @param {*} idList
		 */
		getContentActionList(idList) {
			const contentId = idList[0];
			const content = this.dashboard.getCanvas().getContent(contentId);
			if (this._supportAction(idList, content)) {
				return this._getContributionList(content, false /*shouldCloseOnActionApplied*/);
			} else {
				return [];
			}
		}

		_isSupportedFromVisDefinition(content) {
			const visualization = content.getFeature('Visualization');
			if (!visualization) {
				//todo: the dashboard-feature loader should support to wait content-Visualization feature is ready.
				return false;
			}
			const definition = visualization.getDefinition();
			let supportsDataBehindTheViz = false;
			if (definition.getProperty('isMultilayersWidget') || definition.getProperty('noDataQuery') || this.dashboard.getApplicationName() === 'dashboardTemplate') {
				supportsDataBehindTheViz = false;
			} else {
				supportsDataBehindTheViz = true;
			}
			return supportsDataBehindTheViz;
		}

		/**
		 * pop up data trays if the data tray is not open. If it is open, keep it open
		 * render either the visualization data tab or relationships tab upon opening the tray
		 * @param {boolean} showData If true, render the visualization. Otherwise render the relationship diagram
		 * @return Promise
		 */
		_popUpDataTray(showData = true) {
			let canvasExtensions = this.dashboard.getDashboardCoreSvc('.CanvasExtensions');
			if (canvasExtensions) {
				let splitter = canvasExtensions.splitPaneView.getSplitter();
				let handleIndex = this.dashboard.getApplicationName() === 'story' ? 1 : 0;
				return splitter.toggleItemWithIndexAndBroadcast(handleIndex, true /* always show */, { sender: showData ? 'showDataBehindVisAction' : 'relationships' } );
			} else {
				return Promise.resolve();
			}
		}

		_getContributionList(content, shouldCloseOnActionApplied, isTextOnly = false) {
			const contributionList = [];
			const tooltipId = 'showDataBehindVizLabel';
			const icons = this.features['Icons'] || this.features['Dashboard.Icons'];
			const commonQuerySubject = icons.getIcon('common-querySubject');
			const showData = this._isSupportedFromVisDefinition(content);
			if (showData || this._supportsRelationshipsView()) {
				contributionList.push({
					name: 'contextualGridAction',
					label: StringResources.get(tooltipId),
					text: isTextOnly ? StringResources.get(tooltipId): undefined,
					icon: commonQuerySubject.id,
					type: 'Button',
					group: 'ShowDataBehindVizAction',
					closeOnActionApplied: shouldCloseOnActionApplied,
					section: ContentActionsProviderAPI.ACTION_SECTION.SELECTION_DATA,
					order: 100,
					actions: {
						// render the data for supported visualizations, otherwise render the relationships
						apply: this._popUpDataTray.bind(this, showData)
					}
				});
			}
			return contributionList;
		}

		/**
		 * Check whether a particular interaction is enabled.
		 * Perspective drives this config.
		 *
		 * @param {string} name - interaction name
		 *
		 * @return {boolean} TRUE if interaction is enabled and FALSE if not
		 */
		_isInteractionEnabled(name) {
			const config = this.dashboard.getAppConfig('interactions') || {};
			if (config.hasOwnProperty(name) && [false, 'false'].indexOf(config[name]) !== -1) {
				return false;
			}
			return true;
		}

		getAPI() {
			//todo: we need to implement WidgetAciontsProvider as well once it is ready
			return APIFactory.createAPI(this, [DataPointActionsProviderAPI, ContentActionsProviderAPI]);
		}
	}

	return ContextualGridAction;
});
